import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PasswordChecker, { isValidPassword } from "./PasswordChecker";
import PasswordField from "./PasswordField";
import {
  changeUserPassword,
  ERR_MSG_INVALID_OLD_PASSWORD,
  selectIsChangePasswordInProgress,
  selectLoggedInUser,
} from "../redux/auth";
import { getUserFullName } from "../utils";
import CheckedListItem from "./CheckedListItem";

const Settings = (props) => {
  const dispatch = useDispatch();
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [isOldPasswordValid, setIsOldPasswordValid] = useState(true);
  const [passwordValidate, setPasswordValidate] = useState(false);
  const [showPasswordChecker, setShowPasswordChecker] = useState(false);
  const [showPasswordUpdateSuccess, setShowPasswordUpdateSuccess] =
    useState(false);

  const oldPasswordRef = useRef(null);

  const user = useSelector(selectLoggedInUser);

  const isUpdatingPassword = useSelector(selectIsChangePasswordInProgress);

  const onConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
    setShowPasswordUpdateSuccess(false);
  };

  const onNewPasswordChange = (event) => {
    setNewPassword(event.target.value);
    setShowPasswordChecker(true);
    setShowPasswordUpdateSuccess(false);
  };

  const onOldPasswordChange = (event) => {
    setIsOldPasswordValid(true);
    setOldPassword(event.target.value);
    setShowPasswordUpdateSuccess(false);
  };

  const changePasswordReset = () => {
    setPasswordValidate(false);
    setIsOldPasswordValid(true);
    setShowPasswordChecker(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const onSave = () => {
    setShowPasswordUpdateSuccess(false);
    setPasswordValidate(true);
    if (
      isValidPassword(oldPassword) &&
      isValidPassword(newPassword) &&
      newPassword === confirmPassword
    ) {
      dispatch(changeUserPassword({ newPassword, oldPassword }))
        .unwrap()
        .then(() => {
          changePasswordReset();
          setShowPasswordUpdateSuccess(true);
        })
        .catch((err) => {
          if (err.message === ERR_MSG_INVALID_OLD_PASSWORD) {
            setIsOldPasswordValid(false);
            oldPasswordRef.current.focus();
            oldPasswordRef.current.select();
          }
        });
    }
  };

  return (
    <Box className={props.className}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h5">Profile</Typography>
        </Grid>
        <Grid xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <Grid container spacing={2} className="SettingsSection">
        <Grid xs={12}>
          <TextField
            disabled
            fullWidth
            label="Full Name"
            value={getUserFullName(user)}
          />
        </Grid>
        <Grid xs={12}>
          <TextField disabled fullWidth label="Email" value={user.email} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h5">Change Password</Typography>
        </Grid>
        <Grid xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid className="SettingsSection">
          <Grid className="PasswordCell">
            <PasswordField
              fullWidth
              label="Old Password"
              onChange={onOldPasswordChange}
              ref={oldPasswordRef}
              required
              validate={passwordValidate}
              value={oldPassword}
            />
          </Grid>
          <Grid className="PasswordCell">
            <Divider />
          </Grid>
        </Grid>
        <Grid>
          <List dense disablePadding>
            {!isOldPasswordValid && (
              <CheckedListItem text="Invalid old password" />
            )}
            {showPasswordUpdateSuccess && (
              <CheckedListItem
                checked={true}
                text="User password has been changed"
              />
            )}
          </List>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid className="SettingsSection">
          <Grid className="PasswordCell">
            <PasswordField
              fullWidth
              label="New Password"
              onChange={onNewPasswordChange}
              required
              validate={passwordValidate}
              value={newPassword}
            />
          </Grid>
          <Grid className="PasswordCell">
            <PasswordField
              fullWidth
              label="Confirm Password"
              onChange={onConfirmPasswordChange}
              required
              validate={passwordValidate}
              value={confirmPassword}
            />
          </Grid>
        </Grid>
        <Grid>
          {showPasswordChecker && (
            <PasswordChecker
              className="PasswordChecker"
              confirmPassword={confirmPassword}
              oldPassword={oldPassword}
              password={newPassword}
            />
          )}
        </Grid>
      </Grid>
      <Grid className="SettingsSection" container spacing={2}>
        <Grid xs={12}>
          <Button
            className={isUpdatingPassword ? "InProgress" : ""}
            fullWidth
            disabled={isUpdatingPassword}
            onClick={onSave}
          >
            {isUpdatingPassword ? "Updating..." : "Update Password"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default styled(Settings)(
  ({ theme }) => `
  padding: ${theme.spacing(2)};

  .PasswordCell {
    padding: 4px 0px;
  }

  .SettingsSection {
    width: 400px;
  }
`,
);
